import type {
  ColorSpec,
  ColorTransform,
  ColorTransformOp,
  Effect,
  Fill,
  GradientStop,
  Line,
  LineDash,
  LineEnd,
  SchemeColorName,
  SrcRect,
} from '@slideforge/shared';
import type { Element } from '../xml.js';

/** DrawingML colour, fill, line and effect elements. */

const COLOR_ELEMENTS = ['a:srgbClr', 'a:schemeClr', 'a:sysClr', 'a:prstClr', 'a:scrgbClr'] as const;

const TRANSFORM_OPS: Readonly<Record<string, ColorTransformOp>> = {
  'a:alpha': 'alpha',
  'a:alphaMod': 'alphaMod',
  'a:alphaOff': 'alphaOff',
  'a:hueMod': 'hueMod',
  'a:hueOff': 'hueOff',
  'a:lumMod': 'lumMod',
  'a:lumOff': 'lumOff',
  'a:satMod': 'satMod',
  'a:satOff': 'satOff',
  'a:shade': 'shade',
  'a:tint': 'tint',
  'a:gray': 'gray',
  'a:comp': 'comp',
  'a:inv': 'inv',
};

/** Parses the colour child of a container such as `a:solidFill` or `a:glow`. */
export function parseColorContainer(container: Element | undefined): ColorSpec | undefined {
  if (!container) return undefined;
  const el = container.anyOf(COLOR_ELEMENTS)[0];
  return el ? parseColorElement(el) : undefined;
}

export function parseColorElement(el: Element): ColorSpec | undefined {
  const transforms = parseColorTransforms(el);
  switch (el.name) {
    case 'a:srgbClr':
      return { kind: 'srgb', hex: (el.attr('val') ?? '000000').toUpperCase(), transforms };
    case 'a:schemeClr':
      return { kind: 'scheme', name: (el.attr('val') ?? 'tx1') as SchemeColorName, transforms };
    case 'a:sysClr': {
      const lastHex = el.attr('lastClr');
      return {
        kind: 'system',
        name: el.attr('val') ?? 'windowText',
        ...(lastHex ? { lastHex: lastHex.toUpperCase() } : {}),
        transforms,
      };
    }
    case 'a:prstClr':
      return { kind: 'preset', name: el.attr('val') ?? 'black', transforms };
    case 'a:scrgbClr':
      return {
        kind: 'scrgb',
        r: el.int('r', 0),
        g: el.int('g', 0),
        b: el.int('b', 0),
        transforms,
      };
    default:
      return undefined;
  }
}

function parseColorTransforms(el: Element): ColorTransform[] {
  const out: ColorTransform[] = [];
  for (const child of el.children) {
    const op = TRANSFORM_OPS[child.name];
    if (!op) continue;
    const raw = child.attr('val');
    out.push(raw === undefined ? { op } : { op, value: parsePercent(raw) });
  }
  return out;
}

/**
 * OOXML percentages appear both as thousandths (`50000`) and, in newer files,
 * with an explicit unit (`50%`). Both mean the same thing and both occur in the
 * wild, including inside a single deck.
 */
export function parsePercent(raw: string): number {
  if (raw.endsWith('%')) {
    const n = Number.parseFloat(raw.slice(0, -1));
    return Number.isFinite(n) ? Math.round(n * 1000) : 0;
  }
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : 0;
}

export interface FillContext {
  /** Resolves an `r:embed`/`r:link` id to a media asset id. */
  readonly mediaIdFor: (relId: string) => string | undefined;
  readonly onLinkedMedia?: (relId: string) => void;
}

const FILL_ELEMENTS = [
  'a:noFill',
  'a:solidFill',
  'a:gradFill',
  'a:blipFill',
  'a:pattFill',
  'a:grpFill',
] as const;

/** Reads the fill from a properties element (`p:spPr`, `a:tcPr`, `a:rPr`, ...). */
export function parseFill(props: Element | undefined, ctx: FillContext): Fill {
  if (!props) return { kind: 'inherit' };
  const el = props.anyOf(FILL_ELEMENTS)[0];
  if (!el) return { kind: 'inherit' };
  return parseFillElement(el, ctx);
}

export function parseFillElement(el: Element, ctx: FillContext): Fill {
  switch (el.name) {
    case 'a:noFill':
      return { kind: 'none' };

    case 'a:solidFill': {
      const color = parseColorContainer(el);
      return color ? { kind: 'solid', color } : { kind: 'inherit' };
    }

    case 'a:gradFill': {
      const stops: GradientStop[] = [];
      const list = el.child('a:gsLst');
      for (const gs of list?.all('a:gs') ?? []) {
        const color = parseColorContainer(gs);
        if (!color) continue;
        stops.push({ position: parsePercent(gs.attr('pos') ?? '0') / 100000, color });
      }
      const lin = el.child('a:lin');
      const pathEl = el.child('a:path');
      const rotateWithShape = el.bool('rotWithShape', true);
      if (pathEl) {
        const pathType = pathEl.attr('path');
        return {
          kind: 'gradient',
          stops,
          path:
            pathType === 'circle' || pathType === 'rect' || pathType === 'shape'
              ? pathType
              : 'circle',
          rotateWithShape,
        };
      }
      return {
        kind: 'gradient',
        stops,
        angle: lin ? lin.int('ang', 0) / 60000 : 90,
        scaled: lin ? lin.bool('scaled', false) : false,
        path: 'linear',
        rotateWithShape,
      };
    }

    case 'a:blipFill': {
      const blip = el.child('a:blip');
      const embed = blip?.attr('r:embed');
      const link = blip?.attr('r:link');
      if (!embed && link) ctx.onLinkedMedia?.(link);
      const mediaId = embed ? ctx.mediaIdFor(embed) : undefined;
      if (!mediaId) return { kind: 'inherit' };
      const alphaMod = blip?.child('a:alphaModFix');
      const srcRect = parseSrcRect(el.child('a:srcRect'));
      return {
        kind: 'blip',
        mediaId,
        mode: el.has('a:tile') ? 'tile' : 'stretch',
        ...(srcRect ? { srcRect } : {}),
        ...(alphaMod ? { alpha: parsePercent(alphaMod.attr('amt') ?? '100000') / 100000 } : {}),
      };
    }

    case 'a:pattFill': {
      const fg = parseColorContainer(el.child('a:fgClr'));
      const bg = parseColorContainer(el.child('a:bgClr'));
      if (!fg || !bg) return { kind: 'inherit' };
      return { kind: 'pattern', preset: el.attr('prst') ?? 'pct50', foreground: fg, background: bg };
    }

    case 'a:grpFill':
      return { kind: 'group' };

    default:
      return { kind: 'inherit' };
  }
}

export function parseSrcRect(el: Element | undefined): SrcRect | undefined {
  if (!el) return undefined;
  const rect = {
    l: parsePercent(el.attr('l') ?? '0') / 100000,
    t: parsePercent(el.attr('t') ?? '0') / 100000,
    r: parsePercent(el.attr('r') ?? '0') / 100000,
    b: parsePercent(el.attr('b') ?? '0') / 100000,
  };
  return rect.l || rect.t || rect.r || rect.b ? rect : undefined;
}

const DASH_VALUES = new Set<LineDash>([
  'solid',
  'dot',
  'dash',
  'lgDash',
  'dashDot',
  'lgDashDot',
  'lgDashDotDot',
  'sysDash',
  'sysDot',
  'sysDashDot',
  'sysDashDotDot',
]);

export function parseLine(ln: Element | undefined, ctx: FillContext): Line | undefined {
  if (!ln) return undefined;
  if (ln.has('a:noFill')) return undefined;

  const prstDash = ln.path('a:prstDash')?.attr('val');
  const dash: LineDash = DASH_VALUES.has(prstDash as LineDash) ? (prstDash as LineDash) : 'solid';

  const cap = ln.attr('cap');
  const join = ln.has('a:round') ? 'round' : ln.has('a:bevel') ? 'bevel' : 'miter';
  const compound = ln.attr('cmpd');

  return {
    width: ln.int('w', 0),
    fill: parseFill(ln, ctx),
    dash,
    cap: cap === 'rnd' ? 'rnd' : cap === 'sq' ? 'sq' : 'flat',
    join,
    compound:
      compound === 'dbl' || compound === 'thickThin' || compound === 'thinThick' || compound === 'tri'
        ? compound
        : 'sng',
    ...(parseLineEnd(ln.child('a:headEnd')) ? { headEnd: parseLineEnd(ln.child('a:headEnd')) as LineEnd } : {}),
    ...(parseLineEnd(ln.child('a:tailEnd')) ? { tailEnd: parseLineEnd(ln.child('a:tailEnd')) as LineEnd } : {}),
  };
}

function parseLineEnd(el: Element | undefined): LineEnd | undefined {
  if (!el) return undefined;
  const type = el.attr('type') ?? 'none';
  const known = ['none', 'triangle', 'stealth', 'diamond', 'oval', 'arrow'];
  return {
    type: (known.includes(type) ? type : 'none') as LineEnd['type'],
    width: (el.attr('w') ?? 'med') as LineEnd['width'],
    length: (el.attr('len') ?? 'med') as LineEnd['length'],
  };
}

export function parseEffects(effectLst: Element | undefined): Effect[] {
  if (!effectLst) return [];
  const out: Effect[] = [];
  for (const el of effectLst.children) {
    switch (el.name) {
      case 'a:outerShdw': {
        const color = parseColorContainer(el);
        if (!color) break;
        out.push({
          kind: 'outerShadow',
          blurRadius: el.int('blurRad', 0),
          distance: el.int('dist', 0),
          direction: el.int('dir', 0) / 60000,
          color,
        });
        break;
      }
      case 'a:innerShdw': {
        const color = parseColorContainer(el);
        if (!color) break;
        out.push({
          kind: 'innerShadow',
          blurRadius: el.int('blurRad', 0),
          distance: el.int('dist', 0),
          direction: el.int('dir', 0) / 60000,
          color,
        });
        break;
      }
      case 'a:glow': {
        const color = parseColorContainer(el);
        if (!color) break;
        out.push({ kind: 'glow', radius: el.int('rad', 0), color });
        break;
      }
      case 'a:softEdge':
        out.push({ kind: 'softEdge', radius: el.int('rad', 0) });
        break;
      case 'a:reflection':
        out.push({
          kind: 'reflection',
          blurRadius: el.int('blurRad', 0),
          distance: el.int('dist', 0),
          startAlpha: parsePercent(el.attr('stA') ?? '100000') / 100000,
          endAlpha: parsePercent(el.attr('endA') ?? '0') / 100000,
        });
        break;
      case 'a:blur':
        out.push({ kind: 'blur', radius: el.int('rad', 0), grow: el.bool('grow', true) });
        break;
      default:
        break;
    }
  }
  return out;
}
